/* Customer statements (Duncan 2026-10-15): what each customer owes, from GET /api/invoice-history.

   The customer is CE's own customer number for the job — the server sends it as j.customer, from
   the Customer column of the agent's AR pull (Duncan 2026-10-17: Customer # "the same as CE's
   statement"; BAYLO-M-01 is ARAMARK's) — else the job number's first part: CROSB-D-01 and
   CROSB-D-02 are both Crosby ISD. "Open" is anything CE still shows a balance on: an unpaid (or
   part-paid) invoice, or a credit memo not yet applied (a negative balance). */

export const customerOf = (job) => String(job || '').split('-')[0];
/** A job's customer: its CE customer number (the server's j.customer), else the job number's first part. */
export const customerOfJob = (j) => (j && j.customer) || customerOf(j && j.job);

/* A readable name from the customer's CE job names, for a customer CE's Customer List hasn't
   given us (the agent pulls it: data.customers): the part before " - " ("Ferris ISD - 2025 Bond"
   → "Ferris ISD") without a trailing "E3L" ("Oakwood ISD E3L - Grant" → "Oakwood ISD"); the most
   common across its jobs. The statement lets you correct it before printing. */
export const customerNameOf = (jobNames) => {
  const count = new Map();
  jobNames.forEach((n) => {
    const name = String(n || '').split(/\s+[-–—]\s+/)[0].replace(/\s+E3L$/i, '').trim();
    if (name) count.set(name, (count.get(name) || 0) + 1);
  });
  const best = [...count].sort((a, b) => b[1] - a[1] || a[0].length - b[0].length)[0];
  return best ? best[0] : '';
};

const cents = (n) => Math.round(n * 100) / 100;
/* The invoice a credit memo is numbered after: 142652CR → 142652, and CM142696 → 142696 (the grant
   jobs' reversals, 2026-10-19 -- CE numbers them both ways). */
const creditOf = (number) => String(number).toUpperCase().replace(/^CM/, '').replace(/CR$/, '');
const isOpen = (i) => Math.abs(i.open) > 0.004;
/* A credit memo has no invoice date of its own: it sorts by the day it was applied. */
const dayOf = (i) => i.date || i.paid_on || '';

/* Every customer with something open, biggest balance first. Each item carries its job; `record`
   is the customer's bill-to from CE's Customer List ({ name, address: [lines], phone, fax }), or
   null until the agent has pulled it. */
export function openByCustomer(data) {
  const records = (data && data.customers) || {};
  const by = new Map();
  ((data && data.jobs) || []).forEach((j) => {
    const items = j.invoices.filter(isOpen);
    if (!items.length) return;
    const code = customerOfJob(j);
    const c = by.get(code) || { code, jobs: [], items: [] };
    c.jobs.push({ job: j.job, name: j.name });
    items.forEach((i) => c.items.push({ ...i, job: j.job, jobName: j.name }));
    by.set(code, c);
  });
  return [...by.values()].map((c) => {
    const record = records[c.code] || null;
    // Past due net of the invoice's own credit memo (142652CR against 142652; see agingOf).
    const credit = new Map(c.items.filter((i) => i.open < 0).map((i) => [creditOf(i.number), i.open]));
    const pastDue = c.items.filter((i) => i.open > 0 && i.days_past_due > 0 && i.open + (credit.get(String(i.number).toUpperCase()) || 0) > 0.004);
    return {
      ...c,
      items: c.items.sort((a, b) => dayOf(a).localeCompare(dayOf(b)) || String(a.number).localeCompare(String(b.number))),
      name: (record && record.name) || customerNameOf(c.jobs.map((j) => j.name)) || c.code,
      record,
      balance: cents(c.items.reduce((s, i) => s + i.open, 0)),
      openCount: c.items.filter((i) => i.open > 0).length,
      creditCount: c.items.filter((i) => i.open < 0).length,
      pastDueCount: pastDue.length,
      oldestPastDue: pastDue.reduce((m, i) => Math.max(m, i.days_past_due), 0),
    };
  }).sort((a, b) => b.balance - a.balance || a.name.localeCompare(b.name));
}

/* What billing the statements include (Duncan's call with accounting, 2026-10-19: "these are only
   as of September billings. October billings have not been posted"): an invoice reaches CE's AR --
   and Summit -- only once posted, and a month's billings (dated at its end) are posted in the first
   days of the next, so a statement printed then could leave them out. Last month's billings count
   as posted once any invoice dated from its 24th on is in -- an invoice early in a month (Oct 1, a
   mid-month bill) doesn't say its month-end billing is. Returns the latest posted invoice company-
   wide, last month, and behind (its billings aren't in yet). `today` is 'YYYY-MM-DD'. */
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthName = (ym) => {
  const [y, m] = String(ym || '').split('-').map(Number);
  return y && m ? `${MONTH_NAMES[m - 1]} ${y}` : '';
};
export function postedThrough(data, today) {
  const [y, m] = String(today || '').slice(0, 7).split('-').map(Number);
  const lastMonth = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
  let latest = null;
  let monthEnd = false;
  ((data && data.jobs) || []).forEach((j) => j.invoices.forEach((i) => {
    if (!(i.amount > 0.004) || !i.date) return;
    if (!latest || i.date > latest) latest = i.date;
    if (i.date.slice(0, 7) === lastMonth && Number(i.date.slice(8, 10)) >= 24) monthEnd = true;
  }));
  return { latest, lastMonth, behind: !monthEnd };
}

/* The statement's aging (the buckets of E3's CE statement, Duncan 2026-10-15), by days past due as
   of the AR pull. A credit memo counts where its invoice does (CE numbers it after the invoice:
   142652 → 142652CR, or CM142696 for 142696; both can sit open until AR applies it, as Chico ISD's did on 2026-10-15), so a
   credited invoice doesn't read as past due; any other credit counts as current. */
export const AGING = [
  { key: 'current', label: 'Current', has: (d) => !(d > 0) },
  { key: 'd30', label: '0–30', has: (d) => d >= 1 && d <= 30 },
  { key: 'd60', label: '31–60', has: (d) => d >= 31 && d <= 60 },
  { key: 'd90', label: '61–90', has: (d) => d >= 61 && d <= 90 },
  { key: 'd120', label: '91–120', has: (d) => d >= 91 && d <= 120 },
  { key: 'over', label: '121+', has: (d) => d > 120 },
];
/* Each item's age for the statement: its days past due, or a credit's invoice's. */
export function agedItems(items) {
  const byNumber = new Map(items.map((i) => [String(i.number).toUpperCase(), i]));
  return items.map((i) => {
    if (!(i.open < 0)) return { ...i, age: i.days_past_due };
    const target = byNumber.get(creditOf(i.number));
    return { ...i, age: target && target !== i && target.open > 0 ? target.days_past_due : 0 };
  });
}
export function agingOf(items) {
  const out = Object.fromEntries(AGING.map((b) => [b.key, 0]));
  agedItems(items).forEach((i) => { out[AGING.find((x) => x.has(i.age)).key] += i.open; });
  Object.keys(out).forEach((k) => { out[k] = cents(out[k]); });
  return { ...out, total: cents(items.reduce((s, i) => s + i.open, 0)) };
}
